/**
 * @fileoverview Flagged Transactions — tabbed transaction review modal
 * @module jobs/phases/review/flaggedTransactions/TransactionReviewModal
 *
 * Focused review of one flagged transaction at a stable size (no height
 * jumping between tabs), with previous/next stepping. Tabs:
 *   - Overview — value banner, assessment, window/insolvency callouts,
 *     linked parallel-head chips
 *   - Evidence & Sources — evidence notes, source documents, defence and
 *     recovery callouts
 *   - Notes — multiple practitioner notes (flaggedFlagNotes)
 *   - Activity — the standard ChangeHistory audit trail
 *   - Locks — decision/transaction locks with plain-English explanations
 * "Ask Agent" opens a chat column BESIDE the modal (master agent, primed
 * with this transaction's context) so the conversation and the detail are
 * visible together.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  FileText,
  FolderOpen,
  GitPullRequestArrow,
  History,
  LayoutDashboard,
  Loader2,
  Lock,
  LockOpen,
  MessageSquare,
  Pencil,
  Plus,
  ReceiptText,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/ui/composed/overlays/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/ui/composed/sections/tabs";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "@/components/jobs/phases/gather/summarization/shared-panels/ChangeHistory";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { UserAvatar } from "@/ui/composed/user-avatar";
import { getDisplayName } from "@/ui/composed/user-avatar.helpers";
import {
  ChatMessageList,
  ChatInput,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";
import { Callout } from "./Callout";
import { FlagExportMenu } from "./FlagExportMenu";
import { AgentText, AgentProse, StatuteBadge } from "./AgentText";
import {
  affirmativeDefenceIndicators,
  fmtDate,
  fmtDateTime,
  fmtMoney,
  titleCase,
  stripRecordIds,
  HEAD_ELEMENTS,
  HEAD_RESPONDENT,
  pursuedAgainst,
  PURPOSE_EVIDENCE_BADGE,
  RISK_BADGE,
  DECISION_BADGE,
  type FlagDetailUI,
  type FlagTransactionUI,
  type FlaggedHeadKey,
} from "./flaggedShared";

const FIELD_LABELS: Record<string, string> = {
  risk: "Risk",
  reasoning: "Reasoning",
  quantum: "Recoverable estimate",
  amount: "Amount",
  banding: "Banding",
  decision: "Decision",
  description: "Summary",
  counterpartyName: "Counterparty",
  counterpartyRelationship: "Relationship",
  manualNotes: "Notes",
  recoveryNote: "Recovery note",
  insolvencyBasis: "Insolvency basis",
  purposeBasis: "Circumstances surfaced",
  purposeEvidence: "Material on the file",
  character: "Character",
  characterBasis: "Character basis",
  windowBasis: "Window basis",
  status: "Status",
  supersededReason: "Screened out",
  needsReview: "Needs review",
};

const FIELD_FORMATTERS = {
  amount: (v: unknown) => (typeof v === "number" ? fmtMoney(v) : "—"),
  quantum: (v: unknown) => (typeof v === "number" ? fmtMoney(v) : "—"),
};

export function TransactionReviewModal(props: {
  jobId: Id<"jobs">;
  flagId: Id<"flaggedTransactions">;
  isViewer: boolean;
  onClose: () => void;
  /** Step through the table's rows without closing the modal. */
  onNavigate?: (direction: -1 | 1) => void;
  position?: { index: number; total: number };
  /** Open a sibling flag in this modal (parallel-head chip click). */
  onOpenFlag?: (flagId: string) => void;
  /** Jump to a head view when no sibling flag exists. */
  onNavigateToHead?: (headKey: FlaggedHeadKey) => void;
}) {
  const {
    jobId,
    flagId,
    isViewer,
    onClose,
    onNavigate,
    position,
    onOpenFlag,
    onNavigateToHead,
  } = props;
  const [chatOpen, setChatOpen] = useState(false);
  // Which line's supporting note is open in the side panel (mutually exclusive
  // with the agent chat — they share the modal's side column).
  const [noteTxnId, setNoteTxnId] = useState<string | null>(null);
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);

  const { data: flag } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries.getFlag,
      { jobId, flagId },
    ),
  );
  const f = flag as FlagDetailUI | null | undefined;
  // Heads with a purpose / fault element the agent does not assess: what
  // material is on the file rides in the header badges next to risk and decision.
  const subjectiveElement = f
    ? HEAD_ELEMENTS[f.headKey]?.subjective
    : undefined;
  // Who the claim is pursued against — shown in the header when it isn't the
  // counterparty (director-liability heads), so the counterparty never reads
  // as the target of the claim.
  const respondent = f ? pursuedAgainst(f) : null;

  // Resolve practitioner identities once for the history feed AND the notes.
  const userIds = useMemo<Array<Id<"users">>>(() => {
    const ids = new Set<Id<"users">>();
    for (const change of f?.changeHistory ?? []) {
      const userId = change.userId;
      if (userId) ids.add(userId);
    }
    for (const note of f?.notes ?? []) ids.add(note.userId);
    for (const t of f?.transactions ?? [])
      if (t.lineNoteBy) ids.add(t.lineNoteBy);
    return Array.from(ids);
  }, [f?.changeHistory, f?.notes, f?.transactions]);
  const { data: resolvedUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, { ids: userIds }),
    enabled: userIds.length > 0,
  });
  const usersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of resolvedUsers ?? []) map.set(u._id, u);
    return map;
  }, [resolvedUsers]);

  const setDecision = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .setDecision,
    ),
  });
  const setRowLock = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .setRowLock,
    ),
  });
  const resolveProposed = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .resolveProposedChange,
    ),
  });

  const decide = (decision: "accepted" | "rejected" | "pending") => {
    setDecision.mutate(
      { jobId, flagIds: [flagId], decision },
      {
        onSuccess: () => {
          toast.success(
            decision === "pending"
              ? "Returned to review"
              : `Transaction ${decision}`,
          );
          if (
            decision !== "pending" &&
            onNavigate &&
            position &&
            position.index < position.total - 1
          ) {
            onNavigate(1);
          }
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
      },
    );
  };

  const toggleLock = (level: "row" | "decision", locked: boolean) => {
    setRowLock.mutate(
      { jobId, flagId, locked, level },
      {
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          "gap-0 overflow-hidden p-0 transition-[max-width] duration-200",
          chatOpen || noteTxnId ? "max-w-[96rem]" : "max-w-4xl",
        )}
      >
        <div
          className={cn(
            "grid grid-cols-1 lg:grid-cols-[1fr_auto]",
            chatOpen || noteTxnId
              ? "h-[min(92vh,960px)]"
              : "h-[min(85vh,820px)]",
          )}
        >
          {/* ------------------------------ Modal ------------------------------ */}
          <div className="flex min-h-0 min-w-0 flex-col">
            {!f ? (
              <div className="flex flex-1 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="border-b px-6 pb-3 pt-5">
                  <div className="flex items-start justify-between gap-3 pr-8">
                    <div className="min-w-0">
                      <DialogTitle className="text-base font-semibold leading-snug">
                        {f.counterpartyName}
                      </DialogTitle>
                      <DialogDescription className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                        <span>{f.headLabel}</span>
                        <StatuteBadge>{f.provision}</StatuteBadge>
                        {f.transactionDate && (
                          <span>· {fmtDate(f.transactionDate)}</span>
                        )}
                        {f.directorName && <span>· {f.directorName}</span>}
                      </DialogDescription>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {/* Save this one transaction out as a file note
                          (MOR-808) - Excel / Word / PDF. */}
                      <FlagExportMenu jobId={jobId} flagIds={[flagId]} />
                      {onNavigate && position && (
                        <>
                          <span className="mx-1 text-[11px] tabular-nums text-muted-foreground">
                            {position.index + 1} of {position.total}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={position.index === 0}
                            onClick={() => onNavigate(-1)}
                            title="Previous transaction"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={position.index >= position.total - 1}
                            onClick={() => onNavigate(1)}
                            title="Next transaction"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {f.risk && (
                      <Badge
                        color={RISK_BADGE[f.risk].color}
                        variant="soft"
                        size="sm"
                      >
                        {RISK_BADGE[f.risk].label} Risk
                      </Badge>
                    )}
                    <Badge
                      color={DECISION_BADGE[f.decision].color}
                      variant="soft"
                      size="sm"
                    >
                      {DECISION_BADGE[f.decision].label}
                    </Badge>
                    {f.counterpartyRelationship && (
                      <Badge color="amber" variant="soft" size="sm">
                        {titleCase(f.counterpartyRelationship)}
                      </Badge>
                    )}
                    {/* Respondent differs from the counterparty on
                        director-liability heads — say who the claim is
                        actually against so the counterparty never reads as
                        the target of the claim. */}
                    {respondent && respondent !== f.counterpartyName && (
                      <Badge
                        color="gray"
                        variant="soft"
                        size="sm"
                        title={HEAD_RESPONDENT[f.headKey].note}
                      >
                        Pursued against: {respondent}
                      </Badge>
                    )}
                    {f.valuationRequired && (
                      <Badge color="sky" variant="soft" size="sm">
                        Valuation Required
                      </Badge>
                    )}
                    {/* Subjective-element heads: the rating travels with the
                        flag so a large quantum never reads as a settled claim. */}
                    {subjectiveElement && (
                      <Badge
                        color={
                          f.purposeEvidence
                            ? PURPOSE_EVIDENCE_BADGE[f.purposeEvidence].color
                            : "gray"
                        }
                        variant="soft"
                        size="sm"
                        title={
                          f.purposeEvidence
                            ? PURPOSE_EVIDENCE_BADGE[f.purposeEvidence].detail
                            : `${subjectiveElement.provision} requires ${subjectiveElement.label.toLowerCase()}, which the practitioner assesses. The agent has not recorded what material is on the file.`
                        }
                      >
                        {subjectiveElement.label}:{" "}
                        {f.purposeEvidence
                          ? PURPOSE_EVIDENCE_BADGE[f.purposeEvidence].label
                          : "Not assessed"}
                      </Badge>
                    )}
                    {(f.rowLocked || f.decisionLocked) && (
                      <Badge color="violet" variant="soft" size="sm">
                        <Lock className="mr-1 h-2.5 w-2.5" />
                        {f.rowLocked ? "Locked" : "Decision Locked"}
                      </Badge>
                    )}
                    {f.isManualFlag && (
                      <Badge color="emerald" variant="soft" size="sm">
                        Added Manually
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Tabs */}
                <Tabs
                  defaultValue="overview"
                  className="flex min-h-0 flex-1 flex-col"
                >
                  <TabsList variant="underline" className="shrink-0 px-6">
                    <TabsTrigger variant="underline" value="overview">
                      <LayoutDashboard className="h-3.5 w-3.5" />
                      Overview
                    </TabsTrigger>
                    <TabsTrigger variant="underline" value="transactions">
                      <ReceiptText className="h-3.5 w-3.5" />
                      Transactions
                      {f.transactions.length > 0 && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          {f.transactions.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger variant="underline" value="evidence">
                      <FolderOpen className="h-3.5 w-3.5" />
                      Evidence & Sources
                      {f.evidence.length + f.sourceDocuments.length > 0 && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          {f.evidence.length + f.sourceDocuments.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger variant="underline" value="notes">
                      <StickyNote className="h-3.5 w-3.5" />
                      Notes
                      {f.notes.length > 0 && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          {f.notes.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger variant="underline" value="activity">
                      <History className="h-3.5 w-3.5" />
                      Activity
                      {f.changeHistory.length > 0 && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          {f.changeHistory.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger variant="underline" value="locks">
                      <Lock
                        className={cn(
                          "h-3.5 w-3.5",
                          (f.rowLocked || f.decisionLocked) &&
                            "text-violet-500",
                        )}
                      />
                      Locks
                    </TabsTrigger>
                  </TabsList>

                  <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                    <OverviewTab
                      f={f}
                      jobId={jobId}
                      flagId={flagId}
                      isViewer={isViewer}
                      resolveProposed={resolveProposed}
                      onOpenFlag={onOpenFlag}
                      onNavigateToHead={(headKey) => {
                        onClose();
                        onNavigateToHead?.(headKey);
                      }}
                    />
                    <TransactionsTab
                      f={f}
                      jobId={jobId}
                      flagId={flagId}
                      isViewer={isViewer}
                      onViewDocument={(id) =>
                        setViewingDocumentId(id as Id<"documents">)
                      }
                      onOpenNote={(id) => {
                        setChatOpen(false);
                        setNoteTxnId(id);
                      }}
                    />
                    <EvidenceTab
                      f={f}
                      onViewDocument={(id) =>
                        setViewingDocumentId(id as Id<"documents">)
                      }
                    />
                    <NotesTab
                      f={f}
                      jobId={jobId}
                      flagId={flagId}
                      isViewer={isViewer}
                      usersById={usersById}
                    />
                    <TabsContent value="activity" className="mt-0">
                      {f.changeHistory.length > 0 ? (
                        <ChangeHistory
                          history={f.changeHistory}
                          usersById={usersById}
                          fieldLabels={FIELD_LABELS}
                          formatters={FIELD_FORMATTERS}
                          defaultOpen
                        />
                      ) : (
                        <p className="py-8 text-center text-sm text-muted-foreground">
                          No activity yet.
                        </p>
                      )}
                    </TabsContent>
                    <LocksTab
                      f={f}
                      isViewer={isViewer}
                      onToggle={toggleLock}
                      pending={setRowLock.isPending}
                    />
                  </div>
                </Tabs>

                {/* Footer */}
                <div className="flex flex-wrap items-center gap-2 border-t bg-muted/30 px-6 py-3">
                  {!isViewer && (
                    <>
                      <Button
                        size="sm"
                        className="h-8 gap-1.5"
                        disabled={f.decisionLocked || setDecision.isPending}
                        onClick={() => decide("accepted")}
                      >
                        <Check className="h-3.5 w-3.5" /> Agree
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-8 gap-1.5"
                        disabled={f.decisionLocked || setDecision.isPending}
                        onClick={() => decide("rejected")}
                      >
                        <X className="h-3.5 w-3.5" /> Disagree
                      </Button>
                      {f.decision !== "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1.5"
                          disabled={f.decisionLocked || setDecision.isPending}
                          onClick={() => decide("pending")}
                          title="Clear the decision and put this transaction back into review"
                        >
                          <RotateCcw className="h-3.5 w-3.5" /> Return to Review
                        </Button>
                      )}
                    </>
                  )}
                  <Button
                    size="sm"
                    variant={chatOpen ? "secondary" : "outline"}
                    className="ml-auto h-8 gap-1.5"
                    onClick={() => {
                      setNoteTxnId(null);
                      setChatOpen((o) => !o);
                    }}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    {chatOpen ? "Hide Chat" : "Ask Agent"}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* ------------------------------ Side chat ------------------------------ */}
          {chatOpen && f && (
            <FlagChatColumn
              jobId={jobId}
              flagId={flagId}
              counterpartyName={f.counterpartyName}
              canSend={!isViewer}
            />
          )}

          {/* ---------------------------- Side note panel --------------------------- */}
          {noteTxnId && f && (
            <LineNoteColumn
              key={noteTxnId}
              jobId={jobId}
              flagId={flagId}
              txn={f.transactions.find((t) => t.id === noteTxnId) ?? null}
              usersById={usersById}
              canSave={
                f.amountModel === "sum_of_lines" &&
                !isViewer &&
                !f.decisionLocked &&
                !f.rowLocked
              }
              onClose={() => setNoteTxnId(null)}
            />
          )}
        </div>
      </DialogContent>
      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab(props: {
  f: FlagDetailUI;
  jobId: Id<"jobs">;
  flagId: Id<"flaggedTransactions">;
  isViewer: boolean;
  resolveProposed: {
    mutate: (
      vars: {
        jobId: Id<"jobs">;
        flagId: Id<"flaggedTransactions">;
        accept: boolean;
      },
      opts?: { onSuccess?: () => void },
    ) => void;
  };
  onOpenFlag?: (flagId: string) => void;
  onNavigateToHead: (headKey: FlaggedHeadKey) => void;
}) {
  const {
    f,
    jobId,
    flagId,
    isViewer,
    resolveProposed,
    onOpenFlag,
    onNavigateToHead,
  } = props;

  // Only affirmative defence evidence — drop negation strings the agent may
  // have recorded (e.g. "No s588GA safe-harbour engagement").
  const defenceIndicators = affirmativeDefenceIndicators(f.defenceIndicators);

  const elements = HEAD_ELEMENTS[f.headKey];
  // Heads where liability needs no proof of insolvency: say so outright rather
  // than presenting an insolvency basis the claim does not turn on.
  const noInsolvencyElement = elements?.noInsolvencyElement;
  // Heads with a purpose / fault element: the agent surfaces the circumstances
  // and what material exists, and the callout says the assessment is the
  // practitioner's — no reasoning about the element itself.
  const subjective = elements?.subjective;
  const purposeEvidence = f.purposeEvidence
    ? PURPOSE_EVIDENCE_BADGE[f.purposeEvidence]
    : null;

  // Inline practitioner edit of the recoverable estimate (quantum).
  const [editingQuantum, setEditingQuantum] = useState(false);
  const [quantumDraft, setQuantumDraft] = useState("");
  const editFlag = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .editFlag,
    ),
  });
  const saveQuantum = () => {
    const parsed = Number(quantumDraft.replace(/[$,\s]/g, ""));
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    editFlag.mutate(
      {
        jobId,
        flagId,
        updates: { quantum: parsed },
        reason: "Recoverable estimate set by the practitioner.",
      },
      {
        onSuccess: () => {
          setEditingQuantum(false);
          toast.success("Recoverable estimate updated");
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
      },
    );
  };

  return (
    <TabsContent value="overview" className="mt-0 flex flex-col gap-4">
      <div className="rounded-lg border bg-muted/30 p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Amount
            </div>
            <span className="font-mono text-xl font-bold tabular-nums">
              {fmtMoney(f.amount)}
            </span>
          </div>
          <div className="group/quantum">
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Recoverable Estimate
              {!isViewer && !editingQuantum && (
                <button
                  type="button"
                  className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted hover:text-foreground group-hover/quantum:text-muted-foreground"
                  title="Edit the recoverable estimate"
                  onClick={() => {
                    setQuantumDraft(String(Math.round(f.quantum ?? f.amount)));
                    setEditingQuantum(true);
                  }}
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </div>
            {editingQuantum ? (
              <div className="flex items-center gap-1.5">
                <Input
                  value={quantumDraft}
                  onChange={(e) => setQuantumDraft(e.target.value)}
                  inputMode="decimal"
                  autoFocus
                  className="h-8 w-32 font-mono text-sm tabular-nums"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveQuantum();
                    if (e.key === "Escape") setEditingQuantum(false);
                  }}
                />
                <Button
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={editFlag.isPending}
                  onClick={saveQuantum}
                  title="Save"
                >
                  {editFlag.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => setEditingQuantum(false)}
                  title="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <span
                className="font-mono text-xl font-bold tabular-nums"
                title={
                  f.quantum != null
                    ? "Head-specific recoverable estimate"
                    : "No estimate set — defaults to the gross amount"
                }
              >
                {fmtMoney(f.quantum ?? f.amount)}
                {f.quantum == null && (
                  <span className="ml-1.5 align-middle font-sans text-[10px] font-normal text-muted-foreground">
                    gross
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <AgentProse
          text={f.description}
          className="mt-3 text-sm leading-relaxed"
        />
      </div>

      {f.proposed && (
        <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 dark:border-violet-900 dark:bg-violet-950/20">
          <div className="flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300">
            <GitPullRequestArrow className="h-3.5 w-3.5" />
            Proposed update · {fmtDateTime(f.proposed.proposedAt)}
          </div>
          <AgentProse
            text={f.proposed.reasoning}
            className="mt-1.5 text-sm leading-relaxed"
          />
          {!isViewer && (
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() =>
                  resolveProposed.mutate(
                    { jobId, flagId, accept: true },
                    {
                      onSuccess: () => toast.success("Proposed update applied"),
                    },
                  )
                }
              >
                <Check className="h-3 w-3" /> Agree Update
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1 text-xs"
                onClick={() =>
                  resolveProposed.mutate(
                    { jobId, flagId, accept: false },
                    {
                      onSuccess: () =>
                        toast.success("Proposed update dismissed"),
                    },
                  )
                }
              >
                <X className="h-3 w-3" /> Disagree
              </Button>
            </div>
          )}
        </div>
      )}

      <div>
        <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          Agent Assessment
        </h4>
        {f.reasoning ? (
          <AgentProse text={f.reasoning} className="text-sm leading-relaxed" />
        ) : (
          <p className="text-sm leading-relaxed text-muted-foreground">
            Not yet reviewed by the agent.
          </p>
        )}
      </div>

      {f.windowBasis && (
        <Callout tone="info" title="Review Window">
          <AgentText text={f.windowBasis} />
        </Callout>
      )}
      {noInsolvencyElement ? (
        <Callout tone="legal" title="Insolvency — Not an Element of This Claim">
          <AgentText text={noInsolvencyElement} />
          {f.insolvencyBasis && (
            <p className="mt-1.5 text-muted-foreground">
              Agent note (context only): <AgentText text={f.insolvencyBasis} />
            </p>
          )}
        </Callout>
      ) : (
        f.insolvencyBasis && (
          <Callout tone="legal" title="Insolvency Basis">
            <AgentText text={f.insolvencyBasis} />
          </Callout>
        )
      )}
      {subjective && (
        <Callout
          tone="legal"
          title={`${subjective.label} — For Practitioner Assessment (${subjective.provision})`}
        >
          <p>{subjective.note}</p>
          {purposeEvidence && (
            <p className="mt-1.5">
              <span className="font-medium">
                Material on the file: {purposeEvidence.label}.
              </span>{" "}
              {purposeEvidence.detail}
            </p>
          )}
          {f.purposeBasis ? (
            <div className="mt-1.5">
              <span className="font-medium">Circumstances surfaced: </span>
              <AgentText text={f.purposeBasis} />
            </div>
          ) : (
            <p className="mt-1.5 text-muted-foreground">
              The agent has not recorded the circumstances for this transaction
              yet.
            </p>
          )}
        </Callout>
      )}
      {/* s588FE(5) only extends the reach of a s588FA / s588FB transaction, so
          the character the agent made out is shown as its own element, and its
          absence (a row written before the gate) is called out (MOR-1264). */}
      {f.headKey === "defeat_creditors" &&
        (f.character ? (
          <Callout
            tone="legal"
            title={`Character — ${
              f.character === "s588FA"
                ? "Unfair preference (s588FA)"
                : "Uncommercial transaction (s588FB)"
            }`}
          >
            {f.characterBasis ? (
              <AgentText text={f.characterBasis} />
            ) : (
              <p className="text-muted-foreground">
                The agent named the test but did not record how it applies.
              </p>
            )}
          </Callout>
        ) : (
          <Callout tone="warning" title="No s588FA / s588FB character recorded">
            <p>
              s588FE(5) only extends the reach of an unfair preference (s588FA)
              or an uncommercial transaction (s588FB). This transaction was
              raised without either test being made out, so it is not yet a
              claim under this head. Re-run the head to re-assess it, or reject
              it if neither test can be met.
            </p>
          </Callout>
        ))}
      {defenceIndicators.length > 0 && (
        <Callout
          tone="warning"
          title="Possible Defence Evidence (Surfaced, Not Concluded)"
        >
          <ul className="flex flex-col gap-1">
            {defenceIndicators.map((d, i) => (
              <li key={i}>
                <AgentText text={d} />
              </li>
            ))}
          </ul>
        </Callout>
      )}
      {f.recoveryNote && (
        <Callout tone="recovery" title="Recovery">
          <AgentText text={f.recoveryNote} />
        </Callout>
      )}

      {f.parallelFlags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Also Captured By
          </span>
          {f.parallelFlags.map((p) =>
            p.flagId ? (
              <button
                key={p.headKey}
                type="button"
                className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 text-xs font-medium transition-colors hover:bg-muted/60"
                title={`Open this transaction under ${p.label}`}
                onClick={() => {
                  if (p.flagId && onOpenFlag) onOpenFlag(p.flagId);
                  else onNavigateToHead(p.headKey);
                }}
              >
                {titleCase(p.label)}
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              </button>
            ) : (
              // The agent named the head but that head holds no matching
              // transaction, so there is nothing to open. Say so rather than
              // linking to an empty page (MOR-1265 QA).
              <span
                key={p.headKey}
                className="inline-flex items-center gap-1 rounded-md border border-dashed bg-background px-2 py-0.5 text-xs text-muted-foreground"
                title={`The agent says ${p.label} also reaches this transaction, but that head holds no matching transaction to open.`}
              >
                {titleCase(p.label)}
                <span className="text-[10px]">· no matching transaction</span>
              </span>
            ),
          )}
        </div>
      )}
    </TabsContent>
  );
}

// ---------------------------------------------------------------------------
// Transactions tab — the raw bank entries behind this flag
// ---------------------------------------------------------------------------

function TransactionsTab(props: {
  f: FlagDetailUI;
  jobId: Id<"jobs">;
  flagId: Id<"flaggedTransactions">;
  isViewer: boolean;
  onViewDocument: (documentId: string) => void;
  onOpenNote: (txnId: string) => void;
}) {
  const { f, jobId, flagId, isViewer, onViewDocument, onOpenNote } = props;
  const txns = f.transactions;
  const total = txns.reduce(
    (s, t) => s + (t.direction === "credit" ? t.amount : -t.amount),
    0,
  );
  // Per-line agree/disagree only applies to sum-based aggregates, and only
  // when the practitioner can write and the flag isn't locked.
  const lineReview =
    f.amountModel === "sum_of_lines" &&
    !isViewer &&
    !f.decisionLocked &&
    !f.rowLocked;

  const setLineDecision = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .setLineDecision,
    ),
  });
  const setLineDecisions = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .setLineDecisions,
    ),
  });
  const decideLine = (
    txnId: string,
    decision: "accepted" | "rejected" | "pending",
  ) => {
    setLineDecision.mutate(
      { jobId, flagId, txnId, decision },
      { onError: (e) => toast.error(e.message) },
    );
  };
  // Apply a decision to many lines in one atomic backend write (avoids firing
  // one mutation per row, which raced on the same document).
  const decideMany = (
    txnIds: string[],
    decision: "accepted" | "rejected" | "pending",
    onDone?: () => void,
  ) => {
    if (txnIds.length === 0) return;
    setLineDecisions.mutate(
      { jobId, flagId, txnIds, decision },
      {
        onSuccess: (r) => {
          const verb =
            decision === "accepted"
              ? "agreed"
              : decision === "rejected"
                ? "disagreed"
                : "returned to review";
          const { updated, amount } = r as { updated: number; amount: number };
          toast.success(
            updated > 0
              ? `${updated} transaction${updated === 1 ? "" : "s"} ${verb} — claim now ${fmtMoney(amount)}`
              : `No change — the selected transactions were already ${verb}`,
          );
          onDone?.();
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };
  const decideAll = (decision: "accepted" | "rejected") =>
    decideMany(
      txns.map((t) => t.id),
      decision,
    );

  // Checkbox selection so a practitioner can tick a subset (e.g. select all,
  // then untick a few) and agree / disagree with just those lines.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const allSelected = txns.length > 0 && selectedIds.size === txns.length;
  const someSelected = selectedIds.size > 0;
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(txns.map((t) => t.id)));
  const decideSelected = (decision: "accepted" | "rejected" | "pending") =>
    decideMany([...selectedIds], decision, () => setSelectedIds(new Set()));

  return (
    <TabsContent value="transactions" className="mt-0 flex flex-col gap-3">
      {txns.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No bank entries are linked to this transaction
          {f.isManualFlag ? " — it was added manually" : ""}.
        </p>
      ) : (
        <>
          {lineReview &&
            (someSelected ? (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium text-muted-foreground">
                  {selectedIds.size} selected:
                </span>
                <Button
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  disabled={setLineDecisions.isPending}
                  onClick={() => decideSelected("accepted")}
                >
                  <Check className="h-3 w-3" /> Agree
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-7 gap-1 text-xs"
                  disabled={setLineDecisions.isPending}
                  onClick={() => decideSelected("rejected")}
                >
                  <X className="h-3 w-3" /> Disagree
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 text-xs"
                  disabled={setLineDecisions.isPending}
                  onClick={() => decideSelected("pending")}
                  title="Clear decisions on the selected transactions"
                >
                  <RotateCcw className="h-3 w-3" /> Return to Review
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() => setSelectedIds(new Set())}
                >
                  Clear
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">
                  Decide individual transactions:
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={setLineDecisions.isPending}
                  onClick={() => decideAll("accepted")}
                >
                  Agree all
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={setLineDecisions.isPending}
                  onClick={() => decideAll("rejected")}
                >
                  Disagree all
                </Button>
              </div>
            ))}
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  {lineReview && (
                    <TableHead className="w-8">
                      <Checkbox
                        checked={
                          allSelected
                            ? true
                            : someSelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all transactions"
                      />
                    </TableHead>
                  )}
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Source</TableHead>
                  {f.amountModel === "sum_of_lines" && (
                    <TableHead>Decision</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {txns.map((t) => (
                  <TableRow
                    key={t.id}
                    className={cn(
                      t.documentId && "cursor-pointer",
                      t.lineDecision === "accepted" &&
                        "bg-emerald-50/60 hover:bg-emerald-50 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/30",
                      t.lineDecision === "rejected" &&
                        "line-through opacity-50",
                    )}
                    title={
                      t.documentFileName
                        ? `Open ${t.documentFileName}`
                        : undefined
                    }
                    onClick={() => t.documentId && onViewDocument(t.documentId)}
                  >
                    {lineReview && (
                      <TableCell
                        className="w-8"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedIds.has(t.id)}
                          onCheckedChange={() => toggleSelect(t.id)}
                          aria-label="Select transaction"
                        />
                      </TableCell>
                    )}
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">
                      {fmtDate(t.date)}
                    </TableCell>
                    <TableCell className="text-xs">{t.description}</TableCell>
                    <TableCell
                      className={cn(
                        "whitespace-nowrap text-right font-mono text-xs font-semibold tabular-nums",
                        t.direction === "credit" &&
                          "text-emerald-700 dark:text-emerald-400",
                      )}
                    >
                      {t.direction === "credit" ? "+" : "−"}
                      {fmtMoney(t.amount)}
                    </TableCell>
                    <TableCell>
                      {t.documentFileName ? (
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <FileText className="h-3 w-3 shrink-0" />
                          <span className="max-w-44 truncate">
                            {t.documentFileName}
                          </span>
                          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/40" />
                        </span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground/60">
                          —
                        </span>
                      )}
                    </TableCell>
                    {f.amountModel === "sum_of_lines" && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {lineReview ? (
                          <div className="flex items-center gap-1">
                            {/* Explicit state badge once a decision is made —
                                pending rows stay unbadged (and white). */}
                            {t.lineDecision && t.lineDecision !== "pending" && (
                              <Badge
                                color={DECISION_BADGE[t.lineDecision].color}
                                variant="soft"
                                size="sm"
                                className="mr-1"
                              >
                                {DECISION_BADGE[t.lineDecision].label}
                              </Badge>
                            )}
                            <Button
                              size="sm"
                              variant={
                                t.lineDecision === "accepted"
                                  ? "default"
                                  : "outline"
                              }
                              className="h-6 px-2 text-[11px]"
                              disabled={setLineDecision.isPending}
                              onClick={() =>
                                decideLine(
                                  t.id,
                                  t.lineDecision === "accepted"
                                    ? "pending"
                                    : "accepted",
                                )
                              }
                            >
                              Agree
                            </Button>
                            <Button
                              size="sm"
                              variant={
                                t.lineDecision === "rejected"
                                  ? "destructive"
                                  : "outline"
                              }
                              className="h-6 px-2 text-[11px]"
                              disabled={setLineDecision.isPending}
                              onClick={() =>
                                decideLine(
                                  t.id,
                                  t.lineDecision === "rejected"
                                    ? "pending"
                                    : "rejected",
                                )
                              }
                            >
                              Disagree
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={cn(
                                "h-6 px-1.5",
                                t.lineNote
                                  ? "bg-primary/10 text-primary hover:bg-primary/20"
                                  : "text-muted-foreground/40",
                              )}
                              title={
                                t.lineNote
                                  ? "View / edit supporting note"
                                  : "Add a supporting note"
                              }
                              onClick={() => onOpenNote(t.id)}
                            >
                              <StickyNote
                                className="h-3 w-3"
                                fill={t.lineNote ? "currentColor" : "none"}
                              />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Badge
                              color={
                                DECISION_BADGE[t.lineDecision ?? "pending"]
                                  .color
                              }
                            >
                              {
                                DECISION_BADGE[t.lineDecision ?? "pending"]
                                  .label
                              }
                            </Badge>
                            {t.lineNote && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-1.5 text-primary"
                                title="View supporting note"
                                onClick={() => onOpenNote(t.id)}
                              >
                                <StickyNote className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {txns.length} transaction{txns.length === 1 ? "" : "s"} · net{" "}
            {total < 0 ? "−" : "+"}
            {fmtMoney(Math.abs(total))}
            {/* Disagreed lines are excluded from the claim, so the counted
                figure moves as decisions are made. */}
            {f.amountModel === "sum_of_lines" && (
              <> · included in claim {fmtMoney(f.amount)}</>
            )}{" "}
            · click a row to open the source statement.
          </p>
        </>
      )}
    </TabsContent>
  );
}

// ---------------------------------------------------------------------------
// Evidence tab
// ---------------------------------------------------------------------------

function EvidenceTab(props: {
  f: FlagDetailUI;
  onViewDocument: (documentId: string) => void;
}) {
  const { f, onViewDocument } = props;
  const empty = f.evidence.length === 0 && f.sourceDocuments.length === 0;
  return (
    <TabsContent value="evidence" className="mt-0 flex flex-col gap-4">
      {f.evidence.length > 0 && (
        <div>
          <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Evidence
          </h4>
          <div className="flex flex-col gap-1.5">
            {f.evidence.map((e, i) => {
              const body = (
                <>
                  <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs leading-relaxed">
                      <AgentText text={stripRecordIds(e.note)} />
                    </p>
                    {e.detail && (
                      <p className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                        {e.detail}
                      </p>
                    )}
                    {e.documentFileName ? (
                      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                        {e.documentFileName}
                      </p>
                    ) : (
                      !e.documentId && (
                        <p className="mt-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                          No source recorded — treat as unverified
                        </p>
                      )
                    )}
                  </div>
                </>
              );
              const docId = e.documentId;
              return docId ? (
                <button
                  key={i}
                  type="button"
                  onClick={() => onViewDocument(docId)}
                  className="group flex items-start gap-2.5 rounded-lg border bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted/60"
                  title={`Open ${e.documentFileName ?? "source document"}`}
                >
                  {body}
                  <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                </button>
              ) : (
                <div
                  key={i}
                  className="flex items-start gap-2.5 rounded-lg border bg-muted/30 px-3 py-2"
                >
                  {body}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {f.sourceDocuments.length > 0 && (
        <div>
          <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Source Documents
          </h4>
          <div className="flex flex-col gap-2">
            {f.sourceDocuments.map((doc) => (
              <button
                key={doc.documentId}
                type="button"
                onClick={() => onViewDocument(doc.documentId)}
                className="group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {doc.fileName}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
      {empty && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No evidence recorded yet.
        </p>
      )}
    </TabsContent>
  );
}

// ---------------------------------------------------------------------------
// Notes tab — multiple notes per flag
// ---------------------------------------------------------------------------

function NotesTab(props: {
  f: FlagDetailUI;
  jobId: Id<"jobs">;
  flagId: Id<"flaggedTransactions">;
  isViewer: boolean;
  usersById: Map<Id<"users">, ResolvedHistoryUser>;
}) {
  const { f, jobId, flagId, isViewer, usersById } = props;
  const [draft, setDraft] = useState("");

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const currentUserId = currentUser?._id;

  const addNote = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .addFlagNote,
    ),
  });
  const deleteNote = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .deleteFlagNote,
    ),
  });

  const submit = () => {
    const content = draft.trim();
    if (!content) return;
    addNote.mutate(
      { jobId, flagId, content },
      {
        onSuccess: () => setDraft(""),
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : "Failed to add note"),
      },
    );
  };

  return (
    <TabsContent value="notes" className="mt-0 flex flex-col gap-4">
      {!isViewer && (
        <div className="flex flex-col gap-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Add a note for the file…"
            className="resize-y p-3 text-sm leading-relaxed"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
            }}
          />
          <Button
            size="sm"
            className="h-8 gap-1.5 self-end"
            disabled={addNote.isPending || draft.trim().length === 0}
            onClick={submit}
          >
            {addNote.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Add Note
          </Button>
        </div>
      )}

      {f.notes.length === 0 && !f.manualNotes ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No notes yet.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {f.notes.map((note) => {
            const author = usersById.get(note.userId);
            return (
              <div
                key={note.id}
                className="group flex items-start gap-3 rounded-lg border bg-muted/20 px-3 py-2.5"
              >
                <UserAvatar
                  avatarUrl={author?.avatarUrl}
                  firstName={author?.firstName}
                  lastName={author?.lastName}
                  email={author?.email}
                  className="mt-0.5 h-7 w-7 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium">
                      {author
                        ? getDisplayName(
                            author.firstName,
                            author.lastName,
                            author.email,
                          )
                        : "Practitioner"}
                    </span>
                    <span className="text-[10px] tabular-nums text-muted-foreground">
                      {fmtDateTime(note.createdAt)}
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm leading-relaxed">
                    {note.content}
                  </p>
                </div>
                {!isViewer && note.userId === currentUserId && (
                  <button
                    type="button"
                    className="mt-0.5 rounded p-1 text-muted-foreground/0 transition-colors hover:bg-muted hover:text-red-600 group-hover:text-muted-foreground"
                    title="Delete note"
                    onClick={() =>
                      deleteNote.mutate(
                        { jobId, noteId: note.id },
                        {
                          onError: (e) =>
                            toast.error(
                              e instanceof Error ? e.message : "Failed",
                            ),
                        },
                      )
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          {f.manualNotes && (
            <div className="rounded-lg border border-dashed bg-muted/20 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Earlier Note
              </p>
              <p className="mt-1 whitespace-pre-line text-sm leading-relaxed">
                {f.manualNotes}
              </p>
            </div>
          )}
        </div>
      )}
    </TabsContent>
  );
}

// ---------------------------------------------------------------------------
// Locks tab — plain-English explanations
// ---------------------------------------------------------------------------

function LocksTab(props: {
  f: FlagDetailUI;
  isViewer: boolean;
  pending: boolean;
  onToggle: (level: "row" | "decision", locked: boolean) => void;
}) {
  const { f, isViewer, pending, onToggle } = props;
  return (
    <TabsContent value="locks" className="mt-0 flex flex-col gap-3">
      <LockCard
        title="Decision Lock"
        locked={f.decisionLocked}
        isViewer={isViewer}
        pending={pending}
        onToggle={(locked) => onToggle("decision", locked)}
        description="Freezes your Agree or Disagree decision on this transaction. The agent can still refresh its reasoning and evidence as new data arrives, but anything that would change the decision is parked as a proposed update for you to approve."
      />
      <LockCard
        title="Transaction Lock"
        locked={f.rowLocked}
        isViewer={isViewer}
        pending={pending}
        onToggle={(locked) => onToggle("row", locked)}
        description="Freezes the entire transaction record: risk, reasoning, evidence, amounts, and the decision. Every agent update is parked as a proposed update instead of being applied. Use this once you have settled the position and want nothing to move without your sign-off."
      />
      <Callout tone="legal" title="How Locks Stack">
        Locks apply from the top down: locking the whole case or this analysis
        agent (from the agent's page) freezes every transaction beneath it, the
        Transaction Lock freezes this record, and the Decision Lock freezes only
        the decision. New evidence is never lost — it arrives as a proposed
        update on the Overview tab, and every lock, unlock, and proposal is
        recorded in the Activity tab.
      </Callout>
    </TabsContent>
  );
}

function LockCard(props: {
  title: string;
  description: string;
  locked: boolean;
  isViewer: boolean;
  pending: boolean;
  onToggle: (locked: boolean) => void;
}) {
  const { title, description, locked, isViewer, pending, onToggle } = props;
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{title}</span>
          {locked && (
            <Badge color="violet" variant="soft" size="sm">
              <Lock className="mr-1 h-2.5 w-2.5" />
              Locked
            </Badge>
          )}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      {!isViewer && (
        <Button
          size="sm"
          variant={locked ? "secondary" : "outline"}
          className="h-8 shrink-0 gap-1.5"
          disabled={pending}
          onClick={() => onToggle(!locked)}
        >
          {locked ? (
            <>
              <LockOpen className="h-3.5 w-3.5" /> Unlock
            </>
          ) : (
            <>
              <Lock className="h-3.5 w-3.5" /> Lock
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Side chat column — master agent primed with this transaction
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Line-note side panel — opens beside the modal (like the agent chat)
// ---------------------------------------------------------------------------

function LineNoteColumn(props: {
  jobId: Id<"jobs">;
  flagId: Id<"flaggedTransactions">;
  txn: FlagTransactionUI | null;
  usersById: Map<Id<"users">, ResolvedHistoryUser>;
  canSave: boolean;
  onClose: () => void;
}) {
  const { jobId, flagId, txn, usersById, canSave, onClose } = props;
  // Edit mode opens automatically when there's no note yet (and we can write).
  const [editing, setEditing] = useState(!txn?.lineNote && canSave);
  const [draft, setDraft] = useState(txn?.lineNote ?? "");

  const setLineDecision = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .setLineDecision,
    ),
  });

  const save = () => {
    if (!txn) return;
    setLineDecision.mutate(
      {
        jobId,
        flagId,
        txnId: txn.id,
        decision: txn.lineDecision ?? "pending",
        note: draft.trim(),
      },
      {
        onSuccess: () => {
          toast.success("Note saved");
          setEditing(false);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const author = txn?.lineNoteBy ? usersById.get(txn.lineNoteBy) : undefined;

  return (
    <div className="hidden h-full min-h-0 w-[440px] min-w-0 flex-col border-l bg-muted/10 lg:flex">
      {/* No close button here — the dialog's own top-right X closes everything,
          and the footer carries the panel's Close. */}
      <div className="flex items-center gap-2 border-b py-3 pl-4 pr-10">
        <StickyNote className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium leading-none">
            Supporting note
          </p>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {txn ? `${fmtDate(txn.date)} · ${txn.description}` : "Transaction"}
          </p>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {!txn ? (
          <p className="text-sm text-muted-foreground">
            This bank entry is no longer linked.
          </p>
        ) : (
          <>
            <div className="rounded-lg border bg-background px-3 py-2 text-xs">
              <div className="font-medium">{txn.description}</div>
              <div className="mt-0.5 tabular-nums text-muted-foreground">
                {fmtDate(txn.date)} · {txn.direction === "credit" ? "+" : "−"}
                {fmtMoney(txn.amount)}
              </div>
            </div>
            {editing ? (
              <Textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Why you agree / disagree with this transaction…"
                className="min-h-[160px] flex-1 text-sm"
              />
            ) : txn.lineNote ? (
              <div className="flex items-start gap-3 rounded-lg border bg-background px-3 py-2.5">
                <UserAvatar
                  avatarUrl={author?.avatarUrl}
                  firstName={author?.firstName}
                  lastName={author?.lastName}
                  email={author?.email}
                  className="mt-0.5 h-7 w-7 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium">
                      {author
                        ? getDisplayName(
                            author.firstName,
                            author.lastName,
                            author.email,
                          )
                        : "Practitioner"}
                    </span>
                    {txn.lineNoteAt && (
                      <span className="text-[10px] tabular-nums text-muted-foreground">
                        {fmtDateTime(txn.lineNoteAt)}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">
                    {txn.lineNote}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No note recorded.</p>
            )}
          </>
        )}
      </div>
      {txn && (
        <div className="flex items-center gap-2 border-t p-3">
          {canSave && editing ? (
            <>
              <Button
                size="sm"
                disabled={setLineDecision.isPending}
                onClick={save}
              >
                Save note
              </Button>
              {txn.lineNote && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDraft(txn.lineNote ?? "");
                    setEditing(false);
                  }}
                >
                  Cancel
                </Button>
              )}
            </>
          ) : canSave ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {txn.lineNote ? "Edit note" : "Add note"}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
}

function FlagChatColumn(props: {
  jobId: Id<"jobs">;
  flagId: Id<"flaggedTransactions">;
  counterpartyName: string;
  canSend: boolean;
}) {
  const { jobId, flagId, counterpartyName, canSend } = props;
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const { data: chatMessages } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries
        .listMasterChat,
      { jobId },
    ),
  );
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .sendMasterChatMessage,
    ),
  });

  const realMessages: ChatMessageData[] = useMemo(
    () =>
      (
        (chatMessages ?? []) as Array<{
          id: string;
          role: string;
          body: string;
          createdAt: number;
        }>
      )
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.body,
          createdAt: m.createdAt,
        })),
    [chatMessages],
  );

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  useEffect(() => {
    if (!pendingMessage) return;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content.endsWith(pendingMessage),
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  const lastMessage = messages[messages.length - 1];
  const isThinking = isPending || lastMessage?.role === "user";

  const handleSend = async (raw: string) => {
    const message = raw.trim();
    if (!message || !canSend) return;
    setPendingMessage(message);
    try {
      await sendMessage({ jobId, message, flagId });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
      setPendingMessage(null);
    }
  };

  return (
    <div className="hidden h-full min-h-0 w-[440px] min-w-0 flex-col border-l bg-muted/10 lg:flex">
      {/* pr-10 keeps the dialog's own close button (absolute top-right) clear. */}
      <div className="flex items-center gap-2 border-b py-3 pl-4 pr-10">
        <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium leading-none">
            Agent Chat
          </p>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
            About {counterpartyName}
          </p>
        </div>
      </div>
      <ChatMessageList
        messages={messages}
        userInfo={userInfo}
        isThinking={isThinking}
        emptyHeadline="Ask about this transaction"
        emptyMessage={
          'This chat has access to the context of the transaction and can send investigations to the analysis agent (e.g. ask specific questions like "check whether these payments match the supplier invoice").'
        }
        className="min-h-0 flex-1"
      />
      <div className="border-t p-3">
        <ChatInput
          onSend={(m) => void handleSend(m)}
          isDisabled={!canSend || isPending}
          placeholder={
            canSend ? "Ask about this transaction…" : "Viewers can't chat"
          }
        />
      </div>
    </div>
  );
}
